import React, { useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { X, Zap, Disc3 } from "lucide-react";

const POLL_MS = 10000;
const ROTATE_MS = 20000;
const SHOW_MS = 7000;

const ago = (iso) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hr ago`;
  const d = Math.floor(s / 86400);
  return `${d} day${d > 1 ? "s" : ""} ago`;
};

export const LiveSalesFeed = () => {
  const [toast, setToast] = useState(null);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("cmb_feed_off") === "1");
  const seen = useRef(null);
  const recent = useRef([]);
  const idx = useRef(0);
  const lastShown = useRef(0);
  const hideTimer = useRef(null);

  const show = (sale, live) => {
    clearTimeout(hideTimer.current);
    setToast({ ...sale, live });
    setVisible(true);
    lastShown.current = Date.now();
    hideTimer.current = setTimeout(() => setVisible(false), SHOW_MS);
  };

  useEffect(() => {
    if (dismissed) return undefined;
    const poll = async () => {
      try {
        const { data } = await api.get("/feed/sales");
        recent.current = data;
        if (seen.current === null) { seen.current = new Set(data.map((s) => s.id)); return; }
        const fresh = data.filter((s) => !seen.current.has(s.id));
        fresh.forEach((s) => seen.current.add(s.id));
        if (fresh.length) show(fresh[0], true);
      } catch (e) { /* feed is best-effort */ }
    };
    const rotate = () => {
      const list = recent.current;
      if (!list.length || Date.now() - lastShown.current < ROTATE_MS) return;
      show(list[idx.current % list.length], false);
      idx.current += 1;
    };
    poll();
    const p = setInterval(poll, POLL_MS);
    const r = setInterval(rotate, 2000);
    const first = setTimeout(rotate, 6000);
    return () => { clearInterval(p); clearInterval(r); clearTimeout(first); clearTimeout(hideTimer.current); };
  }, [dismissed]);

  if (dismissed || !toast) return null;
  const close = () => { setVisible(false); setDismissed(true); sessionStorage.setItem("cmb_feed_off", "1"); };

  return (
    <div data-testid="live-sales-feed" aria-live="polite"
      className={`fixed left-4 bottom-28 z-40 w-[calc(100vw-2rem)] max-w-sm transition-[opacity,transform] duration-500 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}>
      <div className="flex items-center gap-3 rounded-2xl border border-[#2a2a2a] bg-[#0f0f0f]/90 backdrop-blur-xl p-3 pr-9 shadow-2xl relative">
        <div className="relative w-12 h-12 rounded-lg bg-[#1e1e1e] shrink-0 flex items-center justify-center overflow-hidden">
          <Disc3 className="w-5 h-5 text-[#2a2a2a]" />
          <img src={`${API}${toast.cover_url}`} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }}
            className="absolute inset-0 w-full h-full object-cover" />
        </div>
        <div className="min-w-0">
          <p data-testid="live-sales-feed-text" className="text-sm leading-snug">
            <span className="font-semibold">{toast.buyer}</span> just bought{" "}
            <span className="text-[#e2ff31] font-medium">'{toast.track_title}'</span>
          </p>
          <p className="text-xs text-[#6b7280] mt-0.5 flex items-center gap-1.5">
            {toast.live && <span data-testid="live-sales-feed-live" className="inline-flex items-center gap-1 text-[#22c55e]"><Zap className="w-3 h-3" fill="currentColor" />Live</span>}
            {toast.live && "·"} {ago(toast.created_at)} · Exclusive
          </p>
        </div>
        <button onClick={close} data-testid="live-sales-feed-close" aria-label="Hide purchase notifications"
          className="absolute top-2 right-2 text-[#6b7280] hover:text-white transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
